import { IAppState } from 'core/interfaces/IAppState';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import { useMemo, useState } from 'react';

import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useShallow } from 'zustand/react/shallow';
import { useGetListOfSchedulesQuery } from '../../schedules/graphql/query';
import { defaultReactQueryConfig } from '../../schedules/helpers/helpers.schedule';
import {
  getAPTAMetricsByService,
  getAPTAMetricsByServiceExport,
  getIncidentAnalyticsCountByAssignee,
  getIncidentAnalyticsCountByAssigneeExport,
  getIncidentAnalyticsCountByPriority,
  getIncidentAnalyticsCountByPriorityExport,
  getIncidentAnalyticsCountByService,
  getIncidentAnalyticsCountByServiceExport,
  getIncidentAnalyticsCountByServiceGroupByPriority,
  getIncidentAnalyticsCountByServiceGroupByPriorityExport,
  getIncidentAnalyticsCountByStatus,
  getIncidentAnalyticsCountByStatusExport,
  getIncidentAnalyticsCountByTeam,
  getIncidentAnalyticsCountByTeamExport,
  getIncidentAnalyticsCountVsTime,
  getIncidentAnalyticsCountVsTimeByPriority,
  getIncidentAnalyticsCountVsTimeByPriorityExport,
  getIncidentAnalyticsCountVsTimeByStatus,
  getIncidentAnalyticsCountVsTimeByStatusExport,
  getIncidentAnalyticsCountVsTimeExport,
  getIncidentAnalyticsFilter,
  getIncidentAnalyticsTotalCount,
  getIncidentAnalyticsXTTA,
  getIncidentAnalyticsXTTAByPriority,
  getIncidentAnalyticsXTTAByPriorityExport,
  getIncidentAnalyticsXTTAByService,
  getIncidentAnalyticsXTTAByServiceExport,
  getIncidentAnalyticsXTTAVsTime,
  getIncidentAnalyticsXTTAVsTimeExport,
  getIncidentAnalyticsXTTR,
  getIncidentAnalyticsXTTRByPriority,
  getIncidentAnalyticsXTTRByPriorityExport,
  getIncidentAnalyticsXTTRByService,
  getIncidentAnalyticsXTTRByServiceExport,
  getIncidentAnalyticsXTTRVsTime,
  getIncidentAnalyticsXTTRVsTimeExport,
  getNoiseReductionByService,
  getNoiseReductionByServiceExport,
  getNoiseReductionByTeam,
  getNoiseReductionByTeamExport,
  getOnCallAnalyticFilter,
  getOnCallHoursPerUser,
  getOnCallHoursPerUserExport,
  getTotalSilencedCount,
} from '../api';
import { BasicBarChartProps, StackedBarChartProps } from '../components/Charts/Bar/types';
import { LineChartProps } from '../components/Charts/Line/types';
import {
  ANALYTIC_V3_EVENTS,
  INCIDENT_PRIORITY_COLORS,
  INCIDENT_PRIORITY_LABELS,
  INCIDENT_STATUS_COLORS,
  INCIDENT_STATUS_LABELS,
} from '../constants';
import {
  formatDateForChart,
  getSortByFromSortData,
  prepareDateRangeForAPI,
  transformFiltersForAPI,
  transformOnCallFiltersForAPI,
} from '../helpers';
import { useAnalyticsV3Context } from './useAnalyticsV3Context';
import { useNoiseReductionTabContext } from './useNoiseReductionTabContext';
import { useOnCallHourContext } from './useOnCallHourContext';
import { useToast } from 'library/atoms';
import { AppTracker } from 'shared/analytics/tracker';

export const useGetAnalyticsFilters = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-v3-filters', teamID],
    queryFn: () => getIncidentAnalyticsFilter({ teamID }),
  });
  return { data, isLoading };
};

export const useGetOnCallHourPerUserFilters = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const { data, isLoading } = useQuery({
    queryKey: ['oncall-v3-filters', teamID],
    queryFn: () => getOnCallAnalyticFilter(teamID),
  });
  return { data, isLoading };
};

export const useGetAnalyticsTotalCount = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const dateRange = useAnalyticsV3Context(state => state.dateRange);
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const filtersForAPI = transformFiltersForAPI(filters);
  const { data, isLoading } = useQuery({
    queryKey: [
      'analytics-v3-total-count',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsTotalCount({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
  });
  return { data, isLoading };
};

export const useGetAnalyticsCountByStatus = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-by-status',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByStatus({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data) {
      return data?.metrics.map(metric => ({
        name: INCIDENT_STATUS_LABELS[metric.status as keyof typeof INCIDENT_STATUS_LABELS],
        value: metric.count,
        color: INCIDENT_STATUS_COLORS[metric.status as keyof typeof INCIDENT_STATUS_COLORS],
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountByStatusExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-status-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByStatusExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByPriorityExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-priority-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByPriorityExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByPriority = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-by-priority',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByPriority({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data) {
      return data?.metrics.map(metric => ({
        name: INCIDENT_PRIORITY_LABELS[metric.priority as keyof typeof INCIDENT_PRIORITY_LABELS],
        value: metric.count,
        color: INCIDENT_PRIORITY_COLORS[metric.priority as keyof typeof INCIDENT_PRIORITY_COLORS],
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountByAssigneeExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-assignee-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByAssigneeExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByAssignee = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-by-assignee',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByAssignee({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data) {
      return data?.metrics.map(metric => ({
        name: capitalize(metric.assigneeName),
        value: metric.count,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};
export const useGetAnalyticsCountByTeam = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: ['analytics-v3-count-by-team', teamID, filtersForAPI, startDate, endDate, timeZone],
    queryFn: () =>
      getIncidentAnalyticsCountByTeam({
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data) {
      return data?.metrics.map(metric => ({
        name: capitalize(metric.teamName),
        value: metric.count,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountByTeamExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-team-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByTeamExport({
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByServiceGroupByPriorityExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-service-group-by-priority-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByServiceGroupByPriorityExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByServiceGroupByPriority = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-by-service-group-by-priority',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByServiceGroupByPriority({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): StackedBarChartProps['data'] {
      const priorities = Object.keys(INCIDENT_PRIORITY_LABELS);

      const result: StackedBarChartProps['data'] = {
        dates: data?.metrics.map(metric => metric.serviceName) ?? [],
        series: priorities.map(priority => ({
          name: INCIDENT_PRIORITY_LABELS[priority as keyof typeof INCIDENT_PRIORITY_LABELS],
          color: INCIDENT_PRIORITY_COLORS[priority as keyof typeof INCIDENT_PRIORITY_COLORS],
          data:
            data?.metrics.map(metric => {
              const priorityCount = metric.countByPriority.find(p => p.priority === priority);
              return priorityCount?.count ?? 0;
            }) ?? [],
        })),
      };
      return result;
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountByServiceExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-by-service-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByServiceExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountByService = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-by-service',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountByService({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data) {
      return data?.metrics.map(metric => ({
        name: metric.serviceName,
        value: metric.count,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountVsTimeByStatusExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time-by-status-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTimeByStatusExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountVsTimeByStatus = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time-by-status',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTimeByStatus({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): StackedBarChartProps['data'] {
      const statuses = Object.keys(INCIDENT_STATUS_LABELS);
      return {
        dates:
          data?.metrics.map(metric => formatDateForChart(metric.date, data.interval, timeZone)) ??
          [],
        series: statuses.map(status => ({
          name: INCIDENT_STATUS_LABELS[status as keyof typeof INCIDENT_STATUS_LABELS],
          color: INCIDENT_STATUS_COLORS[status as keyof typeof INCIDENT_STATUS_COLORS],
          data:
            data?.metrics.map(metric => {
              const statusCount = metric.countByStatus.find(s => s.status === status);
              return statusCount?.count ?? 0;
            }) ?? [],
        })),
      };
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountVsTimeByPriorityExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time-by-priority-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTimeByPriorityExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsCountVsTimeByPriority = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time-by-priority',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTimeByPriority({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): StackedBarChartProps['data'] {
      const priorities = Object.keys(INCIDENT_PRIORITY_LABELS);
      return {
        dates:
          data?.metrics.map(metric => formatDateForChart(metric.date, data.interval, timeZone)) ??
          [],
        series: priorities.map(priority => ({
          name: INCIDENT_PRIORITY_LABELS[priority as keyof typeof INCIDENT_PRIORITY_LABELS],
          color: INCIDENT_PRIORITY_COLORS[priority as keyof typeof INCIDENT_PRIORITY_COLORS],
          data:
            data?.metrics.map(metric => {
              const priorityCount = metric.countByPriority.find(p => p.priority === priority);
              return priorityCount?.count ?? 0;
            }) ?? [],
        })),
      };
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountVsTime = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const filtersForAPI = transformFiltersForAPI(filters);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTime({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): BasicBarChartProps['data'] {
      return data?.metrics.map(metric => ({
        name: formatDateForChart(metric.date, data.interval, timeZone),
        value: metric.count,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsCountVsTimeExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-count-vs-time-export',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsCountVsTimeExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsResponseMetricsXTTA = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading } = useQuery({
    queryKey: [
      'analytics-v3-xtta',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTA({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
  });
  return { data, isLoading };
};

export const useGetAnalyticsResponseMetricsXTTR = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading } = useQuery({
    queryKey: [
      'analytics-v3-xttr',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTR({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate: startDate,
        endDate: endDate,
      }),
  });
  return { data, isLoading };
};

export const useGetAnalyticsXTTAVsTimeExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xtta-vs-time-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAVsTimeExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTAVsTime = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xtta-vs-time',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAVsTime({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): LineChartProps['data'] {
      return {
        dates:
          data?.metrics.map(metric => formatDateForChart(metric.date, data.interval, timeZone)) ??
          [],
        series: data?.metrics.map(metric => metric.xtta) ?? [],
      };
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsXTTRVsTimeExport = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xttr-vs-time-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRVsTimeExport({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTRVsTime = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xttr-vs-time',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRVsTime({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): LineChartProps['data'] {
      return {
        dates:
          data?.metrics.map(metric => formatDateForChart(metric.date, data.interval, timeZone)) ??
          [],
        series: data?.metrics.map(metric => metric.xttr) ?? [],
      };
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsXTTAByPriorityExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xtta-by-priority-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAByPriorityExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTAByPriority = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xtta-by-priority',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAByPriority({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): BasicBarChartProps['data'] {
      return data?.metrics.map(metric => ({
        name: INCIDENT_PRIORITY_LABELS[metric.priority as keyof typeof INCIDENT_PRIORITY_LABELS],
        value: metric.xtta,
        color: INCIDENT_PRIORITY_COLORS[metric.priority as keyof typeof INCIDENT_PRIORITY_COLORS],
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsXTTRByPriorityExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xttr-by-priority-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRByPriorityExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTRByPriority = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xttr-by-priority',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRByPriority({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): BasicBarChartProps['data'] {
      return data?.metrics.map(metric => ({
        name: INCIDENT_PRIORITY_LABELS[metric.priority as keyof typeof INCIDENT_PRIORITY_LABELS],
        value: metric.xttr,
        color: INCIDENT_PRIORITY_COLORS[metric.priority as keyof typeof INCIDENT_PRIORITY_COLORS],
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsXTTAByServiceExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xtta-by-service-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAByServiceExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTAByService = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xtta-by-service',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTAByService({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): BasicBarChartProps['data'] {
      return data?.metrics.map(metric => ({
        name: metric.serviceName,
        value: metric.xtta,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsXTTRByServiceExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-xttr-by-service-export',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRByServiceExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsXTTRByService = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const metricType = useAnalyticsV3Context(useShallow(state => state.responseMetrics.metricType));
  const filtersForAPI = transformFiltersForAPI(filters, metricType);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-xttr-by-service',
      teamID,
      filtersForAPI,
      metricType,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getIncidentAnalyticsXTTRByService({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
    select(data): BasicBarChartProps['data'] {
      return data?.metrics.map(metric => ({
        name: metric.serviceName,
        value: metric.xttr,
      }));
    },
  });
  return { data, isLoading, isError, refetch, error };
};

export const useGetAnalyticsTotalSilencedCount = ({
  isOrgAnalytics = false,
}: {
  isOrgAnalytics?: boolean;
}) => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { data, isLoading } = useQuery({
    queryKey: [
      'analytics-v3-total-silenced-count',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      { isOrgAnalytics },
      timeZone,
    ],
    queryFn: () =>
      getTotalSilencedCount({
        teamID: isOrgAnalytics ? undefined : teamID,
        ...filtersForAPI,
        startDate,
        endDate,
      }),
  });
  return { data, isLoading };
};

export const useGetAnalyticsNoiseReductionByServiceExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const pagination = useNoiseReductionTabContext(state => state.noiseReductionTablepaginationInfo);
  const { pageSize = 10, pageIndex = 1 } = pagination;
  const sortBy = getSortByFromSortData(pagination, 'reductionPercentage');

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-noise-reduction-by-service-export',
      teamID,
      filtersForAPI,
      { pageSize },
      { pageIndex },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getNoiseReductionByServiceExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsNoiseReductionByService = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const pagination = useNoiseReductionTabContext(state => state.noiseReductionTablepaginationInfo);
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const { pageSize = 10, pageIndex = 1 } = pagination;
  const sortBy = getSortByFromSortData(pagination, 'reductionPercentage');
  const { data, isLoading, isRefetching, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-noise-reduction-by-service',
      teamID,
      filtersForAPI,
      { pageSize },
      { pageIndex },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getNoiseReductionByService({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
      }),
  });
  return { data, isLoading, isRefetching, isError, refetch, error };
};

export const useGetAnalyticsAPTAMetricsByServiceExport = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const pagination = useNoiseReductionTabContext(state => state.aptaTablepaginationInfo);
  const { pageSize = 10, pageIndex = 1 } = pagination;
  const sortBy = getSortByFromSortData(pagination, 'aptaSuccessCount');

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-apta-metrics-by-service-export',
      teamID,
      filtersForAPI,
      { pageSize },
      { pageIndex },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getAPTAMetricsByServiceExport({
        teamID,
        ...filtersForAPI,
        startDate,
        endDate,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetAnalyticsAPTAMetricsByService = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const pagination = useNoiseReductionTabContext(state => state.aptaTablepaginationInfo);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { pageSize = 10, pageIndex = 1 } = pagination;

  const filtersForAPI = transformFiltersForAPI(filters);
  const sortBy = getSortByFromSortData(pagination, 'aptaSuccessCount');
  const { data, isLoading, isRefetching, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-apta-metrics-by-service',
      teamID,
      filtersForAPI,
      { pageIndex },
      { pageSize },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getAPTAMetricsByService({
        teamID,
        ...filtersForAPI,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
        startDate,
        endDate,
      }),
  });
  return { data, isLoading, isRefetching, isError, refetch, error };
};

export const useGetAnalyticsNoiseReductionByTeam = () => {
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const pagination = useNoiseReductionTabContext(
    state => state.noiseReductionTeamTablepaginationInfo,
  );
  const { pageSize = 10, pageIndex = 1 } = pagination;
  const sortBy = getSortByFromSortData(pagination, 'reductionPercentage');

  const { data, isLoading, isRefetching, isError, refetch, error } = useQuery({
    queryKey: [
      'analytics-v3-noise-reduction-by-team',
      filtersForAPI,
      { pageSize },
      { pageIndex },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getNoiseReductionByTeam({
        ...filtersForAPI,
        startDate,
        endDate,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
      }),
  });
  return { data, isLoading, isRefetching, isError, refetch, error };
};

export const useGetAnalyticsNoiseReductionByTeamExport = () => {
  const filters = useAnalyticsV3Context(useShallow(state => state.filters));
  const filtersForAPI = transformFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const pagination = useNoiseReductionTabContext(
    state => state.noiseReductionTeamTablepaginationInfo,
  );
  const { pageSize = 10, pageIndex = 1 } = pagination;
  const sortBy = getSortByFromSortData(pagination, 'reductionPercentage');

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-noise-reduction-by-team-export',
      filtersForAPI,
      { pageSize },
      { pageIndex },
      sortBy,
      startDate,
      endDate,
      timeZone,
    ],
    queryFn: () =>
      getNoiseReductionByTeamExport({
        ...filtersForAPI,
        startDate,
        endDate,
        pageSize: pageSize,
        pageNumber: pageIndex,
        sortBy,
      }),
    enabled: false,
  });
  return { isLoading, refetch };
};

export const useGetSchedules = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const { data: allSchedules } = useGetListOfSchedulesQuery(
    {
      filters: {
        teamID: teamID,
      },
    },
    defaultReactQueryConfig,
  );
  const getListOfSchedules = useMemo(() => {
    return allSchedules?.schedules?.map(schedule => {
      return {
        label: schedule.name,
        value: String(schedule.ID),
        meta: {
          teamId: schedule.teamID,
        },
      };
    });
  }, [allSchedules]);
  return { getListOfSchedules };
};
export const useGetOnCallHourPerUserData = () => {
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.onCallUsersFilter));
  const filtersForAPI = transformOnCallFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);
  const pagination = useOnCallHourContext(state => state.onCallUserPerHourTablePaginationInfo);
  const setOnCallData = useOnCallHourContext(state => state.setOnCallUserPerHourData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const toast = useToast();

  const sortBy = getSortByFromSortData(pagination, 'onCallSuccessCount');

  const { data } = useQuery({
    queryKey: [
      'onCallHour-perUser',
      teamID,
      filtersForAPI,
      startDate,
      endDate,
      timeZone,
      pagination.pageIndex,
      pagination.pageSize,
      sortBy,
    ],
    queryFn: () => {
      setIsLoading(true); // ✅ Set loading true jab query start ho
      return getOnCallHoursPerUser(
        {
          owner_id: teamID,
          ...filtersForAPI,
          start_time: startDate,
          end_time: endDate,
          page_number: pagination.pageIndex,
          page_size: pagination.pageSize,
        },
        teamID,
      );
    },
    onSuccess: data => {
      setOnCallData(data);
      setIsLoading(false);
    },
    onError: (err: any) => {
      toast({
        status: 'error',
        text: err.response?.data?.meta?.error_message,
      });
      setIsLoading(false);
    },
  });
  return { data, isLoading };
};

export const useGetOnCallHourPerUserExport = () => {
  const toast = useToast();
  const teamID = useSelector((state: IAppState) => state.organization.selectedTeam.teamId);
  const filters = useAnalyticsV3Context(useShallow(state => state.onCallUsersFilter));
  const filtersForAPI = transformOnCallFiltersForAPI(filters);
  const dateRange = useAnalyticsV3Context(useShallow(state => state.dateRange));
  const { startDate, endDate, timeZone } = prepareDateRangeForAPI(dateRange);

  const { isLoading, refetch } = useQuery({
    queryKey: [
      'analytics-v3-on-call-hour-export',
      teamID,
      filtersForAPI,
      timeZone,
      startDate,
      endDate,
    ],
    queryFn: () => {
      getOnCallHoursPerUserExport(
        { ...filtersForAPI, start_time: startDate, end_time: endDate },
        teamID,
      );
    },
    enabled: false,

    onSuccess: () => {
      AppTracker.track(
        ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED,
        {
          'Analytics Scope': 'Team Analytics',
          'Analytics Type': 'On-Call Analytics',
          'Analytics Tab': 'On-Call Analytics',
          'Chart Title': 'On-Call Users Per Hour',
        },
        true,
      );
      toast({
        status: 'success',
        text: `Your on-call hours per user report is ready. Please check your email`,
      });
    },
  });

  return { isLoading, refetch };
};
